/** Console Analytics requests and shapes (staff-only; PostHog aggregates). */
import type { ConsoleRequest } from './requests.js';

export const ANALYTICS_WINDOWS = [7, 30, 90] as const;
export const analyticsRequest = (windowDays: number): ConsoleRequest => ({
  method: 'GET',
  path: `/api/system/analytics?windowDays=${windowDays}`,
});
export function allAnalyticsRequestShapes(): ConsoleRequest[] {
  return [analyticsRequest(30)];
}

export interface AnalyticsRead {
  readonly state: 'connected' | 'not_configured' | 'permission_denied' | 'unavailable';
  readonly windowDays?: number;
  readonly collecting?: boolean;
  readonly lastEventAt?: string | null;
  readonly totals?: { pageviews: number; visitors: number };
  readonly previousTotals?: { pageviews: number; visitors: number };
  readonly sessions?: { count: number; bounced: number; avgDurationSec: number };
  readonly trend?: readonly { day: string; pageviews: number; visitors: number }[];
  readonly sources?: readonly { source: string; visitors: number }[];
  readonly pages?: readonly { path: string; views: number }[];
  readonly conversions?: readonly { event: string; count: number; people: number }[];
  readonly measuredAt?: string;
}

/**
 * Period-over-period percentage change, as an integer percent. Null when there
 * is no prior-period baseline (0 → anything is "new", not an "∞%" change), so a
 * caller renders "—" rather than a misleading number. This is the one reusable
 * comparison primitive the Command Center reporting components share.
 */
export function pctChange(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Bounce rate as an integer percent of sessions, or null when there are none. */
export function bounceRatePct(bounced: number, sessions: number): number | null {
  if (!Number.isFinite(bounced) || !Number.isFinite(sessions) || sessions <= 0) return null;
  return Math.round((bounced / sessions) * 100);
}

/** Average session duration as a compact m:ss (or '0s'), from seconds. */
export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0s';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/** The founding-application funnel, in order, from the conversion counts. */
export const FUNNEL = [
  ['founding_application_viewed', 'Viewed the application'],
  ['founding_application_started', 'Started'],
  ['founding_application_submitted', 'Submitted'],
] as const;
export const OTHER_CONVERSIONS = [
  ['demo_request_submitted', 'Demo requests'],
  ['cta_clicked', 'Call-to-action clicks'],
  ['founding_application_failed', 'Application errors'],
  ['demo_request_failed', 'Demo request errors'],
] as const;
